import React, { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Icon } from "../";
import {
  useDriverGroups,
  useUpdateDriverGroup,
  useDriverManagerDetail,
} from "../../../hooks";

const DriverGroupTab = ({ userId }) => {
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: driverGroupsData, isLoading } = useDriverGroups(searchTerm);
  const { data: userData } = useDriverManagerDetail(userId);
  const updateDriverGroupMutation = useUpdateDriverGroup();

  const driverGroups = driverGroupsData?.driverGroups || [];
  const currentUser = userData;

  const handleSearchChange = (term) => {
    setSearchInput(term);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
  };

  // Get current user's driver group IDs from administrators array
  const getUserDriverGroupIds = () => {
    if (!currentUser || !driverGroups.length) return [];

    const userGroups = [];
    driverGroups.forEach((group) => {
      const isAdmin = group.administrators?.some(
        (admin) => admin.id === currentUser.id,
      );
      if (isAdmin) {
        userGroups.push(group.id);
      }
    });
    return userGroups;
  };

  const [selectedGroups, setSelectedGroups] = useState([]);

  useEffect(() => {
    if (currentUser && driverGroups.length) {
      const userGroups = getUserDriverGroupIds();
      setSelectedGroups(userGroups);
    }
  }, [currentUser, driverGroups]);

  const handleToggleGroup = async (groupId) => {
    const isSelected = selectedGroups.includes(groupId);
    const previousSelectedGroups = [...selectedGroups];
    let newSelectedGroups;

    if (isSelected) {
      // Remove from selection
      newSelectedGroups = selectedGroups.filter((id) => id !== groupId);
    } else {
      // Add to selection
      newSelectedGroups = [...selectedGroups, groupId];
    }

    // Optimistically update UI
    setSelectedGroups(newSelectedGroups);

    try {
      // Get current administrators for this group (excluding current user)
      const currentGroup = driverGroups.find((g) => g.id === groupId);
      const currentAdminIds =
        currentGroup?.administrators
          ?.filter((admin) => admin.id !== userId)
          .map((admin) => admin.id) || [];

      // Combine with new selection
      const allAdminIds = isSelected
        ? currentAdminIds // Remove current user
        : [...currentAdminIds, userId]; // Add current user

      // Prepare administrators array as JSON string (format: "[72]" or "[72, 73]")
      const administrators =
        allAdminIds.length > 0 ? JSON.stringify(allAdminIds) : null;

      // Update the driver group
      await updateDriverGroupMutation.mutateAsync({
        driverGroupId: groupId,
        data: {
          administrators: administrators,
        },
      });

      // Refetch driver groups to get updated administrator lists
      await queryClient.invalidateQueries({ queryKey: ["driverGroups"] });
    } catch (error) {
      // Revert on error
      setSelectedGroups(previousSelectedGroups);
      console.error("Failed to update driver group:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (driverGroups.length === 0 && !searchTerm) {
    return (
      <div className="py-12 text-center">
        <Icon name="Users" className="mx-auto mb-4 h-12 w-12 text-gray-400" />
        <h3 className="mb-2 text-sm font-medium text-gray-300">
          No Driver Groups Available
        </h3>
        <p className="text-sm text-gray-400">
          There are no driver groups available to assign.
        </p>
      </div>
    );
  }

  const getTypeLabel = (type) => {
    if (type === 0) return "In-house drivers";
    if (type === 1) return "delivery companies";
    return "Unknown";
  };

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="mb-4 rounded-lg border border-gray-800 bg-gray-900 p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0 flex-1">
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Icon name="Search" className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search driver groups..."
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="h-10 w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 pr-10 pl-10 text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
              {searchInput && (
                <button
                  onClick={handleClearSearch}
                  className="absolute top-1/2 right-2 flex h-5 w-5 -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full bg-gray-600 transition-colors hover:bg-gray-500"
                  type="button"
                >
                  <svg
                    className="h-3 w-3 text-gray-300 hover:text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {driverGroups.length === 0 && searchTerm ? (
        <div className="py-12 text-center">
          <Icon
            name="Search"
            className="mx-auto mb-4 h-12 w-12 text-gray-400"
          />
          <h3 className="mb-2 text-sm font-medium text-gray-300">
            No Driver Groups Found
          </h3>
          <p className="text-sm text-gray-400">
            No driver groups match your search criteria.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {driverGroups.map((group) => {
            const isSelected = selectedGroups.includes(group.id);
            const typeLabel = getTypeLabel(group.type);

            return (
              <div
                key={group.id}
                className={`rounded-lg border bg-gray-800 p-4 transition-colors ${
                  isSelected
                    ? "border-blue-500 bg-blue-500/10"
                    : "border-gray-700 hover:border-gray-600"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="mb-2 flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleGroup(group.id)}
                        disabled={updateDriverGroupMutation.isPending}
                        className="h-4 w-4 cursor-pointer rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-2 focus:ring-blue-500"
                      />
                      <h3 className="text-lg font-medium text-white">
                        {group.name}
                      </h3>
                    </div>

                    <div className="ml-7">
                      <div className="flex items-center space-x-2 text-sm text-gray-400">
                        <Icon name="Tag" className="h-4 w-4" />
                        <span>Type: {typeLabel}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DriverGroupTab;
